import { Link } from "react-router-dom";
import { Lock, ArrowRight } from "lucide-react";
import SEO from "@/components/SEO";
import PageHero from "@/components/PageHero";
import CtaBand from "@/components/CtaBand";
import { Reveal, Eyebrow, SectionTitle } from "@/components/Reveal";
import { MANIFESTO, PRIVACY_POINTS } from "@/data/content";

const Privacy = () => (
  <>
    <SEO
      title="Privacy & Confidentiality | Delhi STD Clinic — Dr. Ankur Lal"
      description="How Delhi STD Clinic protects your privacy — confidential consultations, responsible handling of patient and appointment information, and a judgment-free environment."
      path="/privacy-confidentiality"
    />
    <PageHero
      eyebrow="Privacy & Confidentiality"
      title="Your Sexual Health. Your Privacy."
      intro='"We understand that discussing sexual health can feel difficult. Our clinic provides a private, respectful and judgment-free environment where you can discuss your concerns openly with a qualified doctor."'
    />

    <section className="py-14 sm:py-20" data-testid="privacy-points-section">
      <div className="max-w-5xl mx-auto px-5 sm:px-8">
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3.5">
          {PRIVACY_POINTS.map((p, i) => (
            <Reveal key={p} delay={(i % 3) * 0.06}>
              <div
                data-testid={`privacy-point-${i}`}
                className="h-full bg-emerald-50 border border-emerald-900/15 rounded-2xl px-5 py-5 flex items-center gap-3"
              >
                <Lock className="w-4 h-4 text-emerald-900 shrink-0" />
                <span className="text-sm font-semibold text-emerald-950 leading-snug">{p}</span>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    <section className="py-14 sm:py-20 bg-slate-950 text-slate-100" data-testid="privacy-manifesto">
      <div className="max-w-6xl mx-auto px-5 sm:px-8">
        <Reveal className="max-w-2xl">
          <p className="text-xs font-semibold tracking-[0.28em] uppercase text-amber-400/90">
            How We Protect You
          </p>
          <h2 className="mt-4 font-serif text-3xl sm:text-4xl font-semibold text-white">
            Confidentiality, in practice
          </h2>
        </Reveal>
        <div className="mt-12 grid sm:grid-cols-2 gap-x-12 gap-y-10">
          {MANIFESTO.map((m, i) => (
            <Reveal key={m.num} delay={i * 0.07}>
              <div className="flex gap-6 items-start border-t border-white/10 pt-7">
                <span className="font-serif text-5xl font-light text-amber-400/70 leading-none">{m.num}</span>
                <div>
                  <h3 className="font-serif text-xl sm:text-2xl font-semibold text-white">{m.title}</h3>
                  <p className="mt-2 text-sm text-slate-400 leading-relaxed">{m.desc}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    <section className="py-14 sm:py-20" data-testid="privacy-handling">
      <div className="max-w-3xl mx-auto px-5 sm:px-8">
        <Reveal>
          <Eyebrow>Responsible Information Handling</Eyebrow>
          <SectionTitle className="mt-4">How your information is handled</SectionTitle>
          <div className="mt-6 space-y-4 text-slate-600 leading-relaxed">
            <p>
              When you book or consult, we collect only the information needed to arrange
              and provide your care — typically your name, age, contact number and the
              concern you choose to share.
            </p>
            <p>
              Appointment and consultation information is handled with professional
              confidentiality and accessed only as needed for your care. We communicate
              with you only through the contact details you provide.
            </p>
            <p>
              We believe trust is built on honesty rather than impossible promises. We do
              not claim "100% anonymity" or "guaranteed secrecy" — instead, we practice
              genuine, professional medical confidentiality, consistent with medical
              ethics and applicable law.
            </p>
            <p>
              For full details, please read our{" "}
              <Link
                to="/privacy-policy"
                data-testid="privacy-policy-link"
                className="text-emerald-900 font-semibold underline underline-offset-4 inline-flex items-center gap-1"
              >
                Privacy Policy, Terms & Medical Disclaimer <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            </p>
          </div>
        </Reveal>
      </div>
    </section>

    <CtaBand
      title="Consult Privately with Dr. Ankur Lal"
      text="Confidential online and clinic consultations for all sexual-health concerns."
    />
  </>
);

export default Privacy;
